import type { DailyRes, Me, SampleRes } from "../shared/api.ts";
import { WORDS, type Word } from "../shared/words.ts";
import { $, api, esc, logout } from "./http.ts";
import { Warp, type WarpWave } from "./warp.ts";

// ---------- 帳號 ----------

async function loadMe() {
  let me: Me;
  try { me = await api<Me>("/api/me"); } catch { return; }
  const u = me.user;
  const tool = `<a class="btn primary" href="/app">拆解單字</a>`;
  $("acct").innerHTML = tool + (u
    ? `<span class="who">${u.avatar ? `<img class="avatar" src="${esc(u.avatar)}" alt="${esc(u.login)}">` : ""}<span class="who-name">${esc(u.login)}</span></span>${u.isAdmin ? `<a class="btn quiet wide-only" href="/admin">後台</a>` : ""}<button class="btn quiet wide-only" type="button" id="logout">登出</button>`
    : `<a class="btn gh" href="/auth/github?next=/app" aria-label="GitHub 登入"><span class="gh-text">GitHub 登入</span></a>`);
  document.querySelector("#logout")?.addEventListener("click", logout);
  if (!u) $("ctaLogin").innerHTML = `<a class="btn gh big" href="/auth/github?next=/app">用 GitHub 登入</a>`;
  const lim = me.quota.daily.limit;
  if (!u && lim) $("quotaFact").textContent = `問方向和查新的字會用到點數。沒登入每天有 ${lim} 點，用 GitHub 登入可以拿到更多。`;
}

// ---------- 飄浮的字根 ----------
//
// 畫面：所有字根畫在一張 2D 畫布上（每塊先烤成一張小圖，每幀只是貼圖）。
// 互動：每塊字根仍有一個透明的 DOM 元素，負責滑過、點擊、拖曳。
// 波動：合體時用 WebGL 只對波動範圍做折射扭曲，波動結束就停。

/** 一團碰在一起的字根：同一個字的第 from 到第 to 段。 */
interface Clump {
  inst: number; word: Word; from: number; to: number;
  x: number; y: number; vx: number; vy: number; w: number; h: number;
  el: HTMLElement; complete: boolean; expires: number; dying: boolean;
  held: boolean; hover: boolean;
  /** 最近走過的位置（中心點），拿來畫真實的飛行軌跡。 */
  trail: { x: number; y: number; t: number }[];
  /** 被波動推開的位移（只影響畫面，不影響飄的路線），像彈簧一樣回到 0。 */
  ox: number; oy: number; bvx: number; bvy: number;
  sprite: HTMLCanvasElement; pad: number;
  born: number; dieAt: number; pulse0: number;
  /** 正在跟下一塊對接時的靠近程度（0 到 1），越接近 1 縮得越小。 */
  squeeze: number;
}

const sky = $("sky"), pop = $("pop");
/**
 * 輕量版：字根直接用 DOM 顯示，不用畫布和 WebGL（沒有折射、沒有軌跡）。
 * 裝置跑不動時自動切過來，並記在這台裝置上。
 */
const LITE_KEY = "ugw-lite";
let lite = (() => { try { return localStorage.getItem(LITE_KEY) === "1"; } catch { return false; } })();
const debug: number[] | null = location.hash === "#ripple-test" ? [] : null;
const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
let W = innerWidth, H = innerHeight;
const DPR = Math.min(1.5, devicePixelRatio || 1);
let clumps: Clump[] = [];
let inst = 0;
let pool: Word[] = shuffle([...WORDS]);
/** 現在允許互相吸引、組合的字（最多兩個），其他字根只是飄著。 */
const focus = new Set<number>();
const MAX_FOCUS = 2;
let nextPick = performance.now() + 1500;

function shuffle<T>(a: T[]) { for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
const rand = (a: number, b: number) => a + Math.random() * (b - a);
const activeWords = () => W < 640 ? 5 : W < 1100 ? 8 : 12;
const TRAIL_MS = 700, DOCK = 90, GAP = 2, TRAIL_BANDS = 4;
/** 離邊緣 EDGE_IN 以內開始虛化，超出邊緣 EDGE_OUT 時完全虛掉。 */
const EDGE_IN = 70, EDGE_OUT = 60;
const TRAIL_STYLES = Array.from({ length: TRAIL_BANDS }, (_, i) => `rgba(47,75,234,${(((i + 0.5) / TRAIL_BANDS) * 0.16).toFixed(3)})`);
const MAX_SPEED = 55, COMPLETE_SPEED = 28, MIN_SPEED = 16;
const BG = "#F7F9FF", INK = "#1B1F3B", LINE = "#DCE2F4";
const HL = ["#FFE45C", "#FFB8DA", "#A6EFCB", "#B5DDFF"];

function nextWord(): Word {
  const busy = new Set(clumps.map(c => c.word.w));
  for (let k = 0; k < pool.length; k++) {
    const x = pool.shift()!;
    pool.push(x);
    if (!busy.has(x.w) && x.parts.length >= 2) return x;
  }
  return pool[0];
}

/** 更新透明的 DOM（量尺寸、接互動），再把外觀烤成小圖。 */
function draw(c: Clump) {
  c.el.innerHTML = c.word.parts.slice(c.from, c.to + 1).map((p, k) => `<span class="p" style="--hl:var(--hl-${(c.from + k) % 4 + 1})">${esc(p[0])}</span>`).join("");
  c.el.classList.toggle("multi", c.to > c.from);
  c.el.classList.toggle("complete", c.complete);
  c.w = c.el.offsetWidth; c.h = c.el.offsetHeight;
  if (!lite) bake(c);
}

function bake(c: Clump) {
  const fs = parseFloat(getComputedStyle(c.el).fontSize);
  const multi = c.to > c.from;
  c.pad = c.complete ? 28 : 4;
  const cv = c.sprite;
  cv.width = Math.ceil((c.w + c.pad * 2) * DPR); cv.height = Math.ceil((c.h + c.pad * 2) * DPR);
  const x = cv.getContext("2d")!;
  x.setTransform(DPR, 0, 0, DPR, 0, 0);
  x.font = `800 ${fs}px "Bricolage Grotesque", "Noto Sans TC", sans-serif`;
  x.textAlign = "center"; x.textBaseline = "middle";
  if ("letterSpacing" in x) (x as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${-0.01 * fs}px`;
  [...c.el.children].forEach((span, k) => {
    const el = span as HTMLElement, bx = c.pad + el.offsetLeft, by = c.pad + el.offsetTop, bw = el.offsetWidth, bh = el.offsetHeight;
    x.beginPath(); x.roundRect(bx + 0.5, by + 0.5, bw - 1, bh - 1, 7);
    if (multi) {
      if (c.complete) { x.shadowColor = "rgba(47,75,234,0.22)"; x.shadowBlur = 16; x.shadowOffsetY = 6; }
      x.fillStyle = HL[(c.from + k) % 4]; x.fill();
      x.shadowColor = "transparent"; x.shadowBlur = 0; x.shadowOffsetY = 0;
    } else {
      x.fillStyle = "rgba(255,255,255,0.92)"; x.fill();
      x.strokeStyle = LINE; x.lineWidth = 1; x.stroke();
    }
    x.fillStyle = INK;
    x.fillText(el.textContent ?? "", bx + bw / 2, by + bh / 2 + fs * 0.04);
  });
}

function place(c: Clump) {
  c.el.style.transform = `translate3d(${c.x + c.ox}px,${c.y + c.oy}px,0)`;
}

function newClump(word: Word, from: number, to: number, complete: boolean): Clump {
  const el = document.createElement("div");
  el.className = "frag";
  sky.append(el);
  const c: Clump = { inst: 0, word, from, to, x: 0, y: 0, vx: 0, vy: 0, w: 0, h: 0, el, complete, expires: Infinity, dying: false, held: false, hover: false,
    trail: [], ox: 0, oy: 0, bvx: 0, bvy: 0, sprite: document.createElement("canvas"), pad: 0, born: performance.now(), dieAt: 0, pulse0: -1e9, squeeze: 0 };
  draw(c);
  bind(c);
  return c;
}

/** 放一個字的所有字根進場；開場時散在畫面上，之後從邊緣飛進來。 */
function spawn(initial: boolean) {
  const word = nextWord(), id = ++inst;
  word.parts.forEach((_, i) => {
    const c = newClump(word, i, i, false);
    c.inst = id;
    const ang = rand(0, Math.PI * 2), sp = rand(18, 40);
    if (initial) {
      c.x = rand(0, W - c.w); c.y = rand(0, H - c.h);
      c.vx = Math.cos(ang) * sp; c.vy = Math.sin(ang) * sp;
    } else {
      // 從四邊之一飛進來，方向大致朝畫面中間。
      const side = Math.floor(rand(0, 4));
      c.x = side === 0 ? -c.w : side === 1 ? W : rand(0, W - c.w);
      c.y = side === 2 ? -c.h : side === 3 ? H : rand(0, H - c.h);
      const tx = rand(W * 0.2, W * 0.8) - c.x, ty = rand(H * 0.2, H * 0.8) - c.y, d = Math.hypot(tx, ty) || 1;
      c.vx = tx / d * sp * 1.4; c.vy = ty / d * sp * 1.4;
    }
    clumps.push(c);
    place(c);
  });
}

// ---------- 波動 ----------

/**
 * 兩塊靠近時一路往內吸，貼上的瞬間收縮到最底，接著沉沉地放出去。
 * R 是範圍，amp 是折射的位移量（px）；amp0、r0 是合體瞬間吸氣的狀態，讓放出去的波從那裡連續接上。
 */
interface Wave { x: number; y: number; t0: number; R: number; amp: number; amp0: number; r0: number; push: number; lastR: number }
let waves: Wave[] = [];
const EXHALE = 900, SWING = 90;
const WAVE = { small: { R: 230, amp: 9, rin: 60, ampIn: 4, push: 20 }, big: { R: 380, amp: 14, rin: 90, ampIn: 6, push: 34 } };

/** 正在對接的兩塊，這一幀的吸氣狀態。 */
interface Inhale { x: number; y: number; q: number; big: boolean }
let inhales: Inhale[] = [];

const inhaleShape = (h: Inhale): WarpWave => {
  const k = h.big ? WAVE.big : WAVE.small;
  return { x: h.x, y: h.y, r: k.rin * (1 - easeIn(h.q)), amp: -k.ampIn * Math.sin(Math.PI / 2 * h.q), width: 18 };
};

function ripple(x: number, y: number, big: boolean, from?: Inhale) {
  const k = big ? WAVE.big : WAVE.small, inh = from ? inhaleShape(from) : null;
  waves.push({ x, y, t0: performance.now(), R: k.R, amp: k.amp, amp0: inh?.amp ?? 0, r0: inh?.r ?? 0, push: k.push, lastR: 0 });
}

const easeOutCubic = (p: number) => 1 - Math.pow(1 - p, 3);
const easeIn = (p: number) => p * p * p;

/** 某個時間點放出去的波長什麼樣子，給 WebGL 用。 */
function shape(w: Wave, now: number): WarpWave | null {
  const t = now - w.t0, p = t / EXHALE;
  if (p >= 1) return null;
  // 前 90ms 從吸氣的位移平順地翻成往外推，之後隨著擴散很快變弱。
  const amp = (w.amp0 + (w.amp - w.amp0) * Math.min(1, t / SWING)) * Math.pow(1 - p, 2);
  return { x: w.x, y: w.y, r: w.r0 + (w.R - w.r0) * easeOutCubic(p), amp, width: 14 + 46 * p };
}

function stepWaves(now: number, dt: number) {
  waves = waves.filter(w => now - w.t0 < EXHALE);
  for (const w of waves) {
    const r = w.r0 + (w.R - w.r0) * easeOutCubic((now - w.t0) / EXHALE);
    // 波前剛掃過的字根被輕輕推一下。
    for (const c of clumps) {
      if (c.held) continue;
      const dx = c.x + c.w / 2 - w.x, dy = c.y + c.h / 2 - w.y, d = Math.hypot(dx, dy);
      if (d < 1 || d <= w.lastR || d > r) continue;
      const k = w.push * (1 - d / w.R) * 5 / d;
      c.bvx += dx * k; c.bvy += dy * k;
      c.vx += dx / d * w.push * 0.1; c.vy += dy / d * w.push * 0.1;
    }
    w.lastR = r;
  }
  for (const c of clumps) {
    c.bvx += (-110 * c.ox - 18 * c.bvx) * dt; c.bvy += (-110 * c.oy - 18 * c.bvy) * dt;
    c.ox += c.bvx * dt; c.oy += c.bvy * dt;
    if (Math.abs(c.ox) < 0.05 && Math.abs(c.bvx) < 0.05) c.ox = c.bvx = 0;
    if (Math.abs(c.oy) < 0.05 && Math.abs(c.bvy) < 0.05) c.oy = c.bvy = 0;
  }
}

/** 對接時越靠近縮得越小（最小 0.9）；合體後從 0.9 帶一點重量彈回 1。 */
function pulseScale(c: Clump, now: number) {
  const t = now - c.pulse0;
  const release = t >= 0 && t < 900 ? 1 - 0.1 * Math.exp(-8 * t / 1000) * Math.cos(10 * t / 1000) : 1;
  return release * (1 - 0.1 * easeIn(c.squeeze));
}

/** a 的右邊接上 b 的左邊；inh 是貼上前一刻的吸氣狀態。 */
function merge(a: Clump, b: Clump, inh?: Inhale) {
  const la = a.to - a.from + 1, lb = b.to - b.from + 1;
  const cx = a.x + a.w, cy = a.y + a.h / 2, mid = (a.x + b.x + b.w) / 2;
  a.vx = (a.vx * la + b.vx * lb) / (la + lb);
  a.vy = (a.vy * la + b.vy * lb) / (la + lb);
  a.to = b.to;
  a.held = a.held || b.held;
  b.el.remove();
  clumps = clumps.filter(c => c !== b);
  a.complete = a.from === 0 && a.to === a.word.parts.length - 1;
  draw(a);
  // 合體後以原本兩塊的中間為中心，字變大也不會跳位置。
  a.x = mid - a.w / 2; a.y = cy - a.h / 2;
  a.trail = [];
  a.pulse0 = performance.now();
  a.squeeze = 0;
  if (a.complete) {
    a.expires = performance.now() + rand(9000, 13000);
    ripple(mid, cy, true, inh);
  } else {
    ripple(cx, cy, false, inh);
  }
  if (shown === b) showPop(a);
}

function retire(c: Clump) {
  c.dying = true;
  c.dieAt = performance.now();
  focus.delete(c.inst);
  if (shown === c) hidePop();
  setTimeout(() => {
    c.el.remove();
    clumps = clumps.filter(x => x !== c);
    spawn(false);
  }, 900);
}

function step(dt: number, now: number) {
  // 隔一陣子挑一個字開始組合，同時最多兩個。
  for (const id of focus) if (!clumps.some(c => c.inst === id && !c.dying)) focus.delete(id);
  if (focus.size < MAX_FOCUS && now > nextPick) {
    const idle = [...new Set(clumps.filter(c => !c.dying && !focus.has(c.inst)).map(c => c.inst))];
    if (idle.length) focus.add(idle[Math.floor(Math.random() * idle.length)]);
    nextPick = now + rand(3000, 7000);
  }

  // 被挑中的字，順序相鄰的兩團互相吸引：a 的右端被拉向 b 的左端。
  inhales = [];
  for (const c of clumps) c.squeeze = 0;
  for (const a of clumps) {
    if (a.dying || !focus.has(a.inst)) continue;
    const b = clumps.find(c => c.inst === a.inst && c.from === a.to + 1 && !c.dying);
    if (!b) continue;
    const dx = b.x - (a.x + a.w + GAP), dy = (b.y + b.h / 2) - (a.y + a.h / 2), d = Math.hypot(dx, dy) || 1;
    const big = a.from === 0 && b.to === a.word.parts.length - 1;
    const inh: Inhale = { x: a.x + a.w + GAP / 2, y: a.y + a.h / 2, q: Math.max(0, Math.min(1, 1 - d / DOCK)), big };
    if (d < 2.5) { inh.q = 1; merge(a, b, inh); continue; }
    if (d < DOCK) {
      // 一路吸氣：越靠近畫面往內收得越深，兩塊也縮得越小。
      inhales.push(inh);
      a.squeeze = b.squeeze = inh.q;
      // 靠近了就慢慢對齊貼上，不再直接撞進彼此裡面。
      const k = 1 - Math.exp(-7 * dt), share = a.held || b.held ? 1 : 0.5;
      if (!a.held) { a.x += dx * k * share; a.y += dy * k * share; a.vx *= 0.92; a.vy *= 0.92; }
      if (!b.held) { b.x -= dx * k * share; b.y -= dy * k * share; b.vx *= 0.92; b.vy *= 0.92; }
      continue;
    }
    const f = d < 220 ? 60 : 14;
    const ax = dx / d * f * dt, ay = dy / d * f * dt;
    if (!a.held) { a.vx += ax; a.vy += ay; }
    if (!b.held) { b.vx -= ax; b.vy -= ay; }
  }

  for (const c of clumps) {
    if (c.complete && !c.dying && !c.hover && now > c.expires) retire(c);
    if (c.held) continue;
    if (c.hover) { c.vx *= 0.8; c.vy *= 0.8; }
    const max = c.complete ? COMPLETE_SPEED : MAX_SPEED, sp = Math.hypot(c.vx, c.vy);
    if (sp > max) { const k = Math.max(max / sp, 1 - 1.8 * dt); c.vx *= k; c.vy *= k; }
    else if (!c.hover && sp < MIN_SPEED) {
      // 太慢就慢慢加速回來；幾乎停住時隨便挑一個方向繼續飄。
      if (sp < 0.5) { const a = rand(0, Math.PI * 2); c.vx = Math.cos(a); c.vy = Math.sin(a); }
      const k = 1 + 2.5 * dt;
      c.vx *= k; c.vy *= k;
    }
    c.x += c.vx * dt; c.y += c.vy * dt;
    // 可以飛出畫面，超出邊緣一段距離後才慢慢被拉回來。
    const M = 160, pull = 90 * dt;
    if (c.x + c.w < -M) c.vx += pull; else if (c.x > W + M) c.vx -= pull;
    if (c.y + c.h < -M) c.vy += pull; else if (c.y > H + M) c.vy -= pull;
    place(c);
  }
  stepWaves(now, dt);
  const t0 = performance.now();
  paint(now);
  if (debug) debug.push(performance.now() - t0);
  if (shown) positionPop(shown);
}

// ---------- 畫布 ----------

// 兩張全螢幕、同樣大小的畫布疊在一起：平常只顯示 2D 畫布（省電）；
// 有波動時顯示 WebGL 那張，它沒有扭曲的地方跟 2D 畫布逐像素相同，切換時不會抖。
const cv = document.createElement("canvas");
cv.className = "sky-cv";
const g = cv.getContext("2d", { alpha: false })!;
const warp = reduce ? null : Warp.create();
sky.prepend(cv);
if (warp) { warp.canvas.classList.add("sky-cv"); warp.canvas.hidden = true; cv.after(warp.canvas); }

/** 很淡的點陣方格：平常幾乎看不見，波動經過時被折射彎曲，才看得出玻璃感。 */
let dots: CanvasPattern | null = null;
function makeDots() {
  const tile = document.createElement("canvas"), size = 26;
  tile.width = tile.height = Math.round(size * DPR);
  const t = tile.getContext("2d")!;
  t.scale(DPR, DPR);
  t.fillStyle = "#DCE3F6";
  t.beginPath(); t.arc(size / 2, size / 2, 1.15, 0, Math.PI * 2); t.fill();
  dots = g.createPattern(tile, "repeat");
  dots?.setTransform(new DOMMatrix().scale(1 / DPR));
}

function fitCanvas() {
  cv.width = Math.round(W * DPR); cv.height = Math.round(H * DPR);
  makeDots();
}
fitCanvas();

function paintLite(now: number) {
  for (const c of clumps) {
    const k = c.dying ? Math.min(1, (now - c.dieAt) / 900) : 0;
    const inset = Math.min(c.x, W - c.x - c.w, c.y, H - c.y - c.h);
    const e = Math.max(0, Math.min(1, (EDGE_IN - inset) / (EDGE_IN + EDGE_OUT)));
    const s = pulseScale(c, now) * (1 + 0.08 * k);
    c.el.style.transform = `translate3d(${c.x + c.ox}px,${c.y + c.oy}px,0) scale(${s.toFixed(3)})`;
    c.el.style.opacity = (Math.min(1, (now - c.born) / 300) * Math.pow(1 - k, 1.6) * (1 - 0.7 * e)).toFixed(3);
  }
}

function paint(now: number) {
  if (lite) return paintLite(now);
  g.setTransform(DPR, 0, 0, DPR, 0, 0);
  g.globalAlpha = 1;
  g.fillStyle = BG; g.fillRect(0, 0, W, H);
  if (dots) { g.fillStyle = dots; g.fillRect(0, 0, W, H); }

  // 軌跡
  g.lineCap = "round";
  for (const c of clumps) {
    const cx = c.x + c.w / 2, cy = c.y + c.h / 2, tr = c.trail;
    const lastP = tr[tr.length - 1];
    if (!lastP || Math.hypot(cx - lastP.x, cy - lastP.y) > 3) tr.push({ x: cx, y: cy, t: now });
    while (tr.length && now - tr[0].t > TRAIL_MS) tr.shift();
    if (c.complete || tr.length < 2) continue;
    // 依新舊分成四段，各畫一條線（越舊越淡越細），不用每一小段各畫一次。
    for (let band = 0; band < TRAIL_BANDS; band++) {
      const lo = band / TRAIL_BANDS, hi = (band + 1) / TRAIL_BANDS, life = (lo + hi) / 2;
      g.beginPath();
      let open = false;
      for (let i = 1; i < tr.length; i++) {
        const l = 1 - (now - tr[i].t) / TRAIL_MS;
        if (l < lo || l >= hi) { open = false; continue; }
        if (!open) { g.moveTo(tr[i - 1].x, tr[i - 1].y); open = true; }
        g.lineTo(tr[i].x, tr[i].y);
      }
      g.strokeStyle = TRAIL_STYLES[band];
      g.lineWidth = 0.5 + life * 1.2;
      g.stroke();
    }
  }

  // 字根（貼事先烤好的小圖）
  for (const c of clumps) {
    // 消散：一邊模糊、一邊稍微放大、一邊淡出。
    const k = c.dying ? Math.min(1, (now - c.dieAt) / 900) : 0;
    const fadeIn = Math.min(1, (now - c.born) / 300), fadeOut = Math.pow(1 - k, 1.6);
    const s = pulseScale(c, now) * (1 + 0.08 * k), cx = c.x + c.ox + c.w / 2, cy = c.y + c.oy + c.h / 2;
    const sw = c.w + c.pad * 2, sh = c.h + c.pad * 2;
    if (cx + sw < 0 || cx - sw > W || cy + sh < 0 || cy - sh > H) continue;
    // 靠近畫面邊緣就開始虛化：飛出去時由實變虛，飛進來時由虛變實。
    const inset = Math.min(c.x, W - c.x - c.w, c.y, H - c.y - c.h);
    const e = Math.max(0, Math.min(1, (EDGE_IN - inset) / (EDGE_IN + EDGE_OUT)));
    const blur = Math.max(10 * k, 9 * e);
    g.globalAlpha = fadeIn * fadeOut * (1 - 0.7 * e);
    g.filter = blur > 0.05 ? `blur(${(blur * DPR).toFixed(1)}px)` : "none";
    g.setTransform(DPR * s, 0, 0, DPR * s, DPR * cx, DPR * cy);
    g.drawImage(c.sprite, -sw / 2, -sh / 2, sw, sh);
  }
  g.filter = "none";
  g.setTransform(DPR, 0, 0, DPR, 0, 0);
  g.globalAlpha = 1;

  if (!warp) return;
  const live = [...inhales.map(inhaleShape), ...waves.map(w => shape(w, now))].filter((w): w is WarpWave => w != null && w.amp !== 0);
  const on = live.length > 0 || forceWarp;
  if (on) warp.render(cv, W, H, live);
  warp.canvas.hidden = !on;
}
/** 測試用：強制顯示 WebGL 那張，拿來比對兩張畫布是不是逐像素相同。 */
let forceWarp = false, frozen = false;
/** 測試模式下不自動切輕量版（這台測試機沒有顯示卡，一定會被判定太慢）。 */
const debugNoLite = location.hash === "#ripple-test";

/** 量每幀間隔：整體太慢、或合體波動時明顯卡頓，就切到輕量版。 */
const gaps: number[] = [], warpGaps: number[] = [];
let checked = false;
function watchPerf(gap: number) {
  if (lite || debugNoLite) return;
  const warping = warp != null && !warp.canvas.hidden;
  (warping ? warpGaps : gaps).push(gap);
  const median = (a: number[]) => [...a].sort((x, y) => x - y)[a.length >> 1];
  // 第一次只看 30 幀，慢的裝置一兩秒內就切過去；之後每 90 幀檢查一次。
  if (gaps.length >= (checked ? 90 : 30)) { if (median(gaps) > 22) goLite(); gaps.length = 0; checked = true; }
  if (warpGaps.length >= 20) { if (median(warpGaps) > 28) goLite(); warpGaps.length = 0; }
}

function goLite() {
  lite = true;
  try { localStorage.setItem(LITE_KEY, "1"); } catch {}
  sky.classList.add("lite");
  cv.hidden = true;
  if (warp) warp.canvas.hidden = true;
}

let last = 0;
function loop(t: number) {
  const dt = Math.min(0.05, (t - (last || t)) / 1000);
  if (last) watchPerf(t - last);
  last = t;
  if (frozen) paint(t); else step(dt, t);
  requestAnimationFrame(loop);
}

// ---------- 互動：滑過看意思、抓起來丟 ----------

let shown: Clump | null = null;

function showPop(c: Clump) {
  shown = c;
  const parts = c.word.parts.slice(c.from, c.to + 1);
  const formula = `<div class="pf">${parts.map(p => `<span style="--hl:var(--hl-${(c.word.parts.indexOf(p)) % 4 + 1})">${esc(p[0])}<i>${esc(p[1])}</i></span>`).join("+")}</div>`;
  pop.innerHTML = c.complete
    ? `<div class="pw">${esc(c.word.w)}<small>${esc(c.word.pos)}</small></div><div class="pa">${esc(c.word.ans)}</div>${formula}`
    : `${formula}<div class="pn">還差 ${c.word.parts.length - parts.length} 塊才能組成一個字</div>`;
  pop.hidden = false;
  positionPop(c);
}
function hidePop() { shown = null; pop.hidden = true; }

function positionPop(c: Clump) {
  const pw = pop.offsetWidth, ph = pop.offsetHeight;
  let x = c.x + c.w / 2 - pw / 2, y = c.y - ph - 10;
  if (y < 8) y = c.y + c.h + 10;
  x = Math.max(8, Math.min(W - pw - 8, x));
  pop.style.transform = `translate(${x}px,${y}px)`;
}

function bind(c: Clump) {
  const el = c.el;
  let ox = 0, oy = 0, lx = 0, ly = 0, lt = 0, moved = 0;
  el.addEventListener("pointerenter", e => { if (e.pointerType === "mouse") { c.hover = true; showPop(c); } });
  el.addEventListener("pointerleave", e => { if (e.pointerType === "mouse") { c.hover = false; if (shown === c && !c.held) hidePop(); } });
  el.addEventListener("pointerdown", e => {
    el.setPointerCapture(e.pointerId);
    c.held = true; moved = 0;
    ox = e.clientX - c.x; oy = e.clientY - c.y; lx = e.clientX; ly = e.clientY; lt = e.timeStamp;
    c.vx = c.vy = 0;
  });
  el.addEventListener("pointermove", e => {
    if (!c.held) return;
    const dt = Math.max(1, e.timeStamp - lt) / 1000;
    c.vx = (e.clientX - lx) / dt; c.vy = (e.clientY - ly) / dt;
    moved += Math.abs(e.clientX - lx) + Math.abs(e.clientY - ly);
    lx = e.clientX; ly = e.clientY; lt = e.timeStamp;
    c.x = e.clientX - ox; c.y = e.clientY - oy;
    place(c);
  });
  const release = (e: PointerEvent) => {
    if (!c.held) return;
    c.held = false;
    // 放開時沿用拖曳的速度丟出去，太快的話之後會慢慢減速。
    const sp = Math.hypot(c.vx, c.vy);
    if (sp > 600) { c.vx *= 600 / sp; c.vy *= 600 / sp; }
    if (e.timeStamp - lt > 80) { c.vx = c.vy = 0; }
    if (c.complete) c.expires = Math.max(c.expires, performance.now() + 8000);
    if (moved < 6 && e.pointerType !== "mouse") shown === c ? hidePop() : showPop(c);
  };
  el.addEventListener("pointerup", release);
  el.addEventListener("pointercancel", release);
}

document.addEventListener("pointerdown", e => {
  if (shown && !(e.target as HTMLElement).closest(".frag")) hidePop();
});
addEventListener("resize", () => {
  W = innerWidth; H = innerHeight; fitCanvas();
  if (reduce) paint(performance.now());
});

/** 不想要動畫的人：直接擺幾個完整的字，一樣可以滑過去看意思。 */
function still() {
  for (let i = 0; i < activeWords(); i++) {
    const word = nextWord(), c = newClump(word, 0, word.parts.length - 1, true);
    c.inst = ++inst; c.born = -1e9;
    c.x = rand(0, W - c.w); c.y = rand(0, H - c.h);
    clumps.push(c);
    place(c);
  }
  paint(performance.now());
}

async function start() {
  // 今天的五個字和伺服器上已經拆好的字也加進來飄。
  const extra = await Promise.all([
    api<DailyRes>("/api/daily").then(d => d.words, () => []),
    api<SampleRes>("/api/sample").then(d => d.words, () => []),
  ]);
  const have = new Set(pool.map(x => x.w));
  for (const x of extra.flat()) if (!have.has(x.w)) { have.add(x.w); pool.push(x); }
  pool = shuffle(pool);
  await document.fonts.ready;
  if (lite) goLite();
  if (reduce) return still();
  for (let i = 0; i < activeWords(); i++) spawn(true);
  requestAnimationFrame(loop);
}

// ---------- 「怎麼用」示範 ----------

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

async function demo() {
  const word = $("dWord"), type = $("dType"), fb = $("dFb"), steps = [...$("steps").children] as HTMLElement[];
  const step = (i: number) => steps.forEach((li, k) => li.classList.toggle("on", k === i));
  const say = (html: string) => { fb.classList.remove("show"); fb.innerHTML = html; void fb.offsetWidth; fb.classList.add("show"); };
  async function typeIn(t: string) {
    type.textContent = "";
    for (const ch of t) { type.textContent += ch; await sleep(110); }
  }
  if (reduce) {
    word.classList.add("split", "meant"); type.textContent = "很善良、願意幫人";
    say(`<span class="tag s-good">答對了</span><span class="ans">仁慈的；樂善好施的</span>`);
    return;
  }
  // 只有捲到畫面裡才播。
  let visible = false;
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0.3 }).observe($("demo"));
  $("steps").classList.add("playing");
  for (;;) {
    while (!visible) await sleep(300);
    word.classList.remove("split", "meant"); type.textContent = ""; fb.classList.remove("show");
    step(0); await sleep(600);
    await typeIn("有錢人？"); await sleep(500);
    step(1); say(`<span class="tag s-bad">方向不對</span><span>跟錢沒關係。benefit 裡也有 bene，想想看它的意思。</span>`);
    await sleep(2600);
    step(2); fb.classList.remove("show"); word.classList.add("split"); await sleep(1100);
    word.classList.add("meant"); await sleep(1200);
    await typeIn("很善良、願意幫人"); await sleep(400);
    say(`<span class="tag s-good">答對了</span><span>心裡想要對人好，就是這個字。</span>`);
    await sleep(1800);
    step(3); say(`<span class="ans">benevolent　仁慈的；樂善好施的</span>`);
    await sleep(3200);
  }
}

// 調整波動用：網址加 #ripple-test 時可以從主控台呼叫 __ripple(x, y, big)。
if (debug) {
  // 測試模式：角落顯示 FPS 和每幀繪製時間，按兩下畫面任一處觸發一次大波動。
  const hud = document.createElement("div");
  hud.style.cssText = "position:fixed;right:8px;bottom:8px;z-index:9;font:12px/1.5 ui-monospace,monospace;background:rgba(27,31,59,.85);color:#fff;padding:6px 9px;border-radius:8px;pointer-events:none;white-space:pre";
  document.body.append(hud);
  let frames = 0, t0 = performance.now(), worst = 0;
  const tick = (t: number) => { frames++; requestAnimationFrame(tick); };
  requestAnimationFrame(tick);
  setInterval(() => {
    const now = performance.now(), ms = debug.splice(0);
    const avg = ms.length ? ms.reduce((a, b) => a + b, 0) / ms.length : 0;
    worst = ms.length ? Math.max(...ms) : 0;
    hud.textContent = `FPS ${(frames * 1000 / (now - t0)).toFixed(0)}\n繪製 平均 ${avg.toFixed(1)}ms 最慢 ${worst.toFixed(1)}ms\nWebGL ${warp ? (warp.canvas.hidden ? "待命" : "運作中") : "不支援"}`;
    frames = 0; t0 = now;
  }, 500);
  addEventListener("dblclick", e => ripple(e.clientX, e.clientY, true));
}
if (debug) Object.assign(window, { __ripple: ripple, __paint: debug, __forceWarp: (v: boolean) => { forceWarp = v; }, __freeze: (v: boolean) => { frozen = v; }, __lite: goLite });

loadMe();
start();
demo();
